/* ------------------------------------------------------------------ *
 * The simulated result of ICEWATCH's imaging command: a set of
 * multispectral images of a glacier front, compared with a reference
 * image from a year earlier to measure how far the front has retreated,
 * plus the latest change rates of the other glaciers the mission watches.
 * The glaciers are real; all numbers are simulated (plausible, not
 * measured). Deterministic per group, so all members see the same result.
 * ------------------------------------------------------------------ */

export type GlacierTarget = { name: string; region: string; latDeg: number; lonDeg: number };

// A sample target; one of the fastest-flowing glaciers on Earth.
// Position approximate.
export const GLACIER_TARGET: GlacierTarget = {
  name: "Sermeq Kujalleq (Jakobshavn Glacier)",
  region: "West Greenland",
  latDeg: 69.17,
  lonDeg: -49.83,
};

export type GlacierSite = { name: string; region: string; retreatMPerYear: number };

// The image shown as the result (public/glacier.png). Its fronts, years and
// retreat are drawn into it, so the numbers here must match it.
export const GLACIER_IMAGE = { url: "/glacier.png", referenceYear: 2025, currentYear: 2026, retreatM: 370 };

export type GlacierCapture = {
  target: GlacierTarget;
  imageUrl: string;
  referenceYear: number;
  currentYear: number;
  frames: number;
  bands: string[];
  cloudCoverPct: number;
  // Front retreat since the reference image a year earlier, and the area of
  // ice lost with it.
  retreatM: number;
  areaLossKm2: number;
  frontWidthKm: number;
  sites: GlacierSite[];
  dataKb: number;
};

const OTHER_SITES: { name: string; region: string; range: [number, number] }[] = [
  { name: "Helheim Glacier", region: "Southeast Greenland", range: [60, 450] },
  { name: "Kangerlussuaq Glacier", region: "East Greenland", range: [40, 380] },
  { name: "Zachariae Isstrøm", region: "Northeast Greenland", range: [120, 600] },
  { name: "Petermann Glacier", region: "Northwest Greenland", range: [10, 250] },
];

function random(seed: number) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function generateGlacierCapture(seed: number, target: GlacierTarget = GLACIER_TARGET): GlacierCapture {
  const rnd = random(seed ^ 0x1ce);
  const between = (min: number, max: number) => min + rnd() * (max - min);

  const frames = 6 + Math.floor(rnd() * 4);
  const bands = ["Blue", "Green", "Red", "Near-infrared"];
  const retreatM = GLACIER_IMAGE.retreatM;
  const frontWidthKm = Math.round(between(4.5, 6.5) * 10) / 10;
  const sites: GlacierSite[] = [
    { name: target.name, region: target.region, retreatMPerYear: retreatM },
    ...OTHER_SITES.map((s) => ({ name: s.name, region: s.region, retreatMPerYear: Math.round(between(s.range[0], s.range[1]) / 10) * 10 })),
  ].sort((a, b) => b.retreatMPerYear - a.retreatMPerYear);

  return {
    target,
    imageUrl: GLACIER_IMAGE.url,
    referenceYear: GLACIER_IMAGE.referenceYear,
    currentYear: GLACIER_IMAGE.currentYear,
    frames,
    bands,
    // The image is cloud-free.
    cloudCoverPct: Math.round(between(1, 6)),
    retreatM,
    areaLossKm2: Math.round(((retreatM * frontWidthKm) / 1000) * 10) / 10,
    frontWidthKm,
    sites,
    // About 1.5 MB per band and frame after onboard compression.
    dataKb: Math.round(frames * bands.length * 1.5 * 1024),
  };
}
